import apiUtils from '../utils/apiUtils';
import { logger } from '../utils/logger';

// Live Stream types (matching backend types)
export enum LiveStreamStatus {
  SCHEDULED = 'scheduled',
  LIVE = 'live',
  ENDED = 'ended',
  CANCELLED = 'cancelled',
}

export enum LiveStreamEmoji {
  HEART = '❤️',
  FIRE = '🔥',
  CLAP = '👏',
  WOW = '😍',
  LAUGH = '😂',
  THUMBS_UP = '👍',
  STAR = '⭐',
  FASHION = '👗',
}

export interface EmojiMessage {
  id: string;
  emoji: LiveStreamEmoji;
  userId: string;
  username?: string;
  timestamp: number;
  x?: number;
  y?: number;
}

export enum LiveStreamErrorCode {
  STREAM_NOT_FOUND = 'STREAM_NOT_FOUND',
  STREAM_ALREADY_ACTIVE = 'STREAM_ALREADY_ACTIVE',
  STREAM_CAPACITY_EXCEEDED = 'STREAM_CAPACITY_EXCEEDED',
  STREAM_ENDED = 'STREAM_ENDED',
  INVALID_STREAM_KEY = 'INVALID_STREAM_KEY',
  CONTENT_VIOLATION = 'CONTENT_VIOLATION',
  USER_BLOCKED = 'USER_BLOCKED',
  IVS_SERVICE_ERROR = 'IVS_SERVICE_ERROR',
  AD_SERVICE_ERROR = 'AD_SERVICE_ERROR',
  EMOJI_RATE_LIMIT = 'EMOJI_RATE_LIMIT',
  SCHEDULING_CONFLICT = 'SCHEDULING_CONFLICT',
}

export interface LiveStream {
  streamId: string;
  userId: string;
  title: string;
  description?: string;
  scheduledAt?: string;
  startedAt?: string;
  endedAt?: string;
  status: LiveStreamStatus;
  
  // IVS Integration
  ivsChannelArn: string;
  ivsStreamKey: string;
  ivsPlaybackUrl: string;
  ivsChatRoomArn: string;
  
  // Metrics
  maxViewers: number;
  totalViewers: number;
  duration?: number;
  
  // Ad Integration
  adSlotId?: string;
  totalAdRevenue?: number;
  creatorRevenue?: number;
  
  // Metadata
  createdAt: string;
  updatedAt: string;
}

export interface StreamCredentials {
  streamKey: string;
  ingestEndpoint: string;
  playbackUrl: string;
  chatToken?: string;
}

export interface ViewerCredentials {
  playbackUrl: string;
  chatToken: string;
}

export interface StreamSummary {
  streamId: string;
  title: string;
  duration: number;
  maxViewers: number;
  totalViewers: number;
  totalEmojis: number;
  totalAdRevenue: number;
  creatorRevenue: number;
}

export interface ScheduleStreamRequest {
  title: string;
  description?: string;
  scheduledAt: string; // ISO timestamp
}

export interface DiscoverStreamsResponse {
  liveStreams: LiveStream[];
  scheduledStreams: LiveStream[];
  trendingStreams: LiveStream[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
    hasMore: boolean;
  };
}

/**
 * Custom error class for live streaming operations
 */
export class LiveStreamError extends Error {
  constructor(
    public code: LiveStreamErrorCode,
    message: string,
    public details?: any
  ) {
    super(message);
    this.name = 'LiveStreamError';
  }
}

/**
 * Rate limiter for emoji reactions
 */
class EmojiRateLimiter {
  private lastEmojiTime = 0;
  private readonly RATE_LIMIT_MS = 1000; // 1 emoji per second

  canSendEmoji(): boolean {
    const now = Date.now();
    return now - this.lastEmojiTime >= this.RATE_LIMIT_MS;
  }

  recordEmojiSent(): void {
    this.lastEmojiTime = Date.now();
  }

  getTimeUntilNextEmoji(): number {
    const now = Date.now();
    const timeSinceLastEmoji = now - this.lastEmojiTime;
    return Math.max(0, this.RATE_LIMIT_MS - timeSinceLastEmoji);
  }
}

/**
 * Service for live streaming operations
 */
export class LiveStreamService {
  private emojiRateLimiter = new EmojiRateLimiter();

  /**
   * Schedule a live stream
   * @param request Stream scheduling details
   * @returns Promise with created stream
   */
  async scheduleStream(request: ScheduleStreamRequest): Promise<LiveStream> {
    try {
      logger.info('Scheduling live stream:', request);
      
      const response = await apiUtils.post<LiveStream>('/live/schedule', request);
      
      logger.info('Stream scheduled successfully:', response.streamId);
      return response;
    } catch (error: any) {
      logger.error('Failed to schedule stream:', error);
      
      // Handle specific error codes
      if (error.response?.data?.code === LiveStreamErrorCode.SCHEDULING_CONFLICT) {
        throw new LiveStreamError(
          LiveStreamErrorCode.SCHEDULING_CONFLICT,
          'You already have a stream scheduled at this time. Please choose a different time.',
          error.response.data
        );
      }
      
      if (error.response?.data?.code === LiveStreamErrorCode.STREAM_ALREADY_ACTIVE) {
        throw new LiveStreamError(
          LiveStreamErrorCode.STREAM_ALREADY_ACTIVE,
          'You already have an active stream. Please end your current stream before scheduling a new one.',
          error.response.data
        );
      }
      
      throw new LiveStreamError(
        LiveStreamErrorCode.IVS_SERVICE_ERROR,
        error.response?.data?.message || 'Failed to schedule stream. Please try again.',
        error.response?.data
      );
    }
  }

  /**
   * Start a scheduled live stream
   * @param streamId Stream ID to start
   * @returns Promise with streaming credentials
   */
  async startStream(streamId: string): Promise<StreamCredentials> {
    try {
      logger.info('Starting scheduled live stream:', streamId);
      
      const response = await apiUtils.post<{ stream: LiveStream; credentials: StreamCredentials }>(`/live/start`, { streamId });
      
      logger.info('Scheduled stream started successfully:', streamId);
      return response.credentials;
    } catch (error: any) {
      logger.error('Failed to start stream:', error);
      
      // Handle specific error codes
      if (error.response?.data?.code === LiveStreamErrorCode.STREAM_NOT_FOUND) {
        throw new LiveStreamError(
          LiveStreamErrorCode.STREAM_NOT_FOUND,
          'Stream not found. It may have been cancelled or expired.',
          error.response.data
        );
      }
      
      if (error.response?.data?.code === LiveStreamErrorCode.USER_BLOCKED) {
        throw new LiveStreamError(
          LiveStreamErrorCode.USER_BLOCKED,
          error.response.data.message || 'You are temporarily blocked from streaming due to content violations.',
          error.response.data
        );
      }
      
      throw new LiveStreamError(
        LiveStreamErrorCode.IVS_SERVICE_ERROR,
        error.response?.data?.message || 'Failed to start stream. Please try again.',
        error.response?.data
      );
    }
  }

  /**
   * Create and start an instant live stream
   * @param title Stream title
   * @param description Optional stream description
   * @returns Promise with stream info and streaming credentials
   */
  async createInstantStream(title: string, description?: string): Promise<{ stream: LiveStream; credentials: StreamCredentials }> {
    try {
      logger.info('Creating instant live stream:', { title, description });
      
      const response = await apiUtils.post<{ stream: LiveStream; credentials: StreamCredentials }>(`/live/start`, { 
        title: title.trim(),
        description: description?.trim()
      });
      
      logger.info('Instant stream created successfully:', response.stream.streamId);
      return response;
    } catch (error: any) {
      logger.error('Failed to create instant stream:', error);
      
      // Handle specific error codes
      if (error.response?.data?.code === LiveStreamErrorCode.USER_BLOCKED) {
        throw new LiveStreamError(
          LiveStreamErrorCode.USER_BLOCKED,
          error.response.data.message || 'You are temporarily blocked from streaming due to content violations.',
          error.response.data
        );
      }
      
      throw new LiveStreamError(
        LiveStreamErrorCode.IVS_SERVICE_ERROR,
        error.response?.data?.message || 'Failed to create instant stream. Please try again.',
        error.response?.data
      );
    }
  }

  /**
   * End a live stream
   * @param streamId Stream ID to end
   * @returns Promise with stream summary
   */
  async endStream(streamId: string): Promise<StreamSummary> {
    try {
      logger.info('Ending live stream:', streamId);
      
      const response = await apiUtils.post<StreamSummary>(`/live/end`, { streamId });
      
      logger.info('Stream ended successfully:', streamId);
      return response;
    } catch (error: any) {
      logger.error('Failed to end stream:', error);
      
      if (error.response?.data?.code === LiveStreamErrorCode.STREAM_NOT_FOUND) {
        throw new LiveStreamError(
          LiveStreamErrorCode.STREAM_NOT_FOUND,
          'Stream not found or already ended.',
          error.response.data
        );
      }
      
      throw new LiveStreamError(
        LiveStreamErrorCode.IVS_SERVICE_ERROR,
        error.response?.data?.message || 'Failed to end stream. Please try again.',
        error.response?.data
      );
    }
  }

  /**
   * Join a live stream as a viewer
   * @param streamId Stream ID to join
   * @returns Promise with viewer credentials
   */
  async joinStream(streamId: string): Promise<ViewerCredentials> {
    try {
      logger.info('Joining live stream:', streamId);
      
      const response = await apiUtils.post<{ credentials: ViewerCredentials; message: string }>(`/live/join/${streamId}`, {});
      
      logger.info('Joined stream successfully:', streamId);
      return response.credentials;
    } catch (error: any) {
      logger.error('Failed to join stream:', error);
      
      // Handle specific error codes
      if (error.response?.data?.code === LiveStreamErrorCode.STREAM_NOT_FOUND) {
        throw new LiveStreamError(
          LiveStreamErrorCode.STREAM_NOT_FOUND,
          'This stream is no longer available.',
          error.response.data
        );
      }
      
      if (error.response?.data?.code === LiveStreamErrorCode.STREAM_ENDED) {
        throw new LiveStreamError(
          LiveStreamErrorCode.STREAM_ENDED,
          'This stream has ended. Check out other live streams!',
          error.response.data
        );
      }
      
      if (error.response?.data?.code === LiveStreamErrorCode.STREAM_CAPACITY_EXCEEDED) {
        throw new LiveStreamError(
          LiveStreamErrorCode.STREAM_CAPACITY_EXCEEDED,
          'This stream is at capacity. Please try again later.',
          error.response.data
        );
      }
      
      throw new LiveStreamError(
        LiveStreamErrorCode.IVS_SERVICE_ERROR,
        error.response?.data?.message || 'Failed to join stream. Please try again.',
        error.response?.data
      );
    }
  }

  /**
   * Leave a live stream
   * @param streamId Stream ID to leave
   * @returns Promise that resolves when successfully left
   */
  async leaveStream(streamId: string): Promise<void> {
    try {
      logger.info('Leaving live stream:', streamId);
      
      await apiUtils.post(`/live/leave/${streamId}`, {});
      
      logger.info('Left stream successfully:', streamId);
    } catch (error: any) {
      logger.error('Failed to leave stream:', error);
      // Don't throw error for leave operations - it's not critical
    }
  }

  /**
   * Send emoji reaction to live stream
   * @param streamId Stream ID
   * @param emoji Emoji to send
   * @returns Promise that resolves when emoji is sent
   */
  async sendEmoji(streamId: string, emoji: LiveStreamEmoji): Promise<void> {
    try {
      // Check rate limit
      if (!this.emojiRateLimiter.canSendEmoji()) {
        const timeUntilNext = this.emojiRateLimiter.getTimeUntilNextEmoji();
        throw new LiveStreamError(
          LiveStreamErrorCode.EMOJI_RATE_LIMIT,
          `Please wait ${Math.ceil(timeUntilNext / 1000)} second(s) before sending another emoji.`
        );
      }
      
      logger.info('Sending emoji to stream:', { streamId, emoji });
      
      await apiUtils.post(`/live/emoji`, { streamId, emoji });
      
      // Record emoji sent for rate limiting
      this.emojiRateLimiter.recordEmojiSent();
      
      logger.info('Emoji sent successfully:', { streamId, emoji });
    } catch (error: any) {
      logger.error('Failed to send emoji:', error);
      
      if (error instanceof LiveStreamError) {
        throw error;
      }
      
      if (error.response?.data?.code === LiveStreamErrorCode.EMOJI_RATE_LIMIT) {
        throw new LiveStreamError(
          LiveStreamErrorCode.EMOJI_RATE_LIMIT,
          'You are sending emojis too quickly. Please slow down.',
          error.response.data
        );
      }
      
      throw new LiveStreamError(
        LiveStreamErrorCode.IVS_SERVICE_ERROR,
        error.response?.data?.message || 'Failed to send emoji. Please try again.',
        error.response?.data
      );
    }
  }

  /**
   * Discover live streams
   * @param options Discovery options
   * @returns Promise with discovered streams
   */
  async discoverStreams(options: {
    limit?: number;
    offset?: number;
    includeScheduled?: boolean;
    includeTrending?: boolean;
  } = {}): Promise<DiscoverStreamsResponse> {
    try {
      const {
        limit = 20,
        offset = 0,
        includeScheduled = true,
        includeTrending = true,
      } = options;

      logger.info('Discovering live streams:', options);
      
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        includeScheduled: includeScheduled.toString(),
        includeTrending: includeTrending.toString(),
      });

      const response = await apiUtils.get<DiscoverStreamsResponse>(`/live/discover?${params}`);
      
      logger.info('Discovered streams:', {
        liveCount: response.liveStreams.length,
        scheduledCount: response.scheduledStreams.length,
        trendingCount: response.trendingStreams.length,
      });
      
      return response;
    } catch (error: any) {
      logger.error('Failed to discover streams:', error);
      
      throw new LiveStreamError(
        LiveStreamErrorCode.IVS_SERVICE_ERROR,
        error.response?.data?.message || 'Failed to discover streams. Please try again.',
        error.response?.data
      );
    }
  }

  /**
   * Get stream details by ID
   * Uses the discover endpoint and filters for the specific stream.
   * @param streamId Stream ID
   * @returns Promise with stream details
   */
  async getStream(streamId: string): Promise<LiveStream> {
    try {
      logger.info('Getting stream details:', streamId);
      
      // Use the discover endpoint to find the stream
      const response = await this.discoverStreams({
        limit: 100,
        includeScheduled: true,
        includeTrending: true,
      });
      
      // Search across all stream categories
      const allStreams = [
        ...(response.liveStreams || []),
        ...(response.scheduledStreams || []),
        ...(response.trendingStreams || [])
      ];
      
      const stream = allStreams.find(s => s.streamId === streamId);
      
      if (!stream) {
        throw new LiveStreamError(
          LiveStreamErrorCode.STREAM_NOT_FOUND,
          'Stream not found.'
        );
      }
      
      logger.info('Retrieved stream details:', streamId);
      return stream;
    } catch (error: any) {
      logger.error('Failed to get stream details:', error);
      
      if (error instanceof LiveStreamError) {
        throw error;
      }
      
      throw new LiveStreamError(
        LiveStreamErrorCode.IVS_SERVICE_ERROR,
        error.response?.data?.message || 'Failed to get stream details. Please try again.',
        error.response?.data
      );
    }
  }

  /**
   * Cancel a scheduled stream (using end stream endpoint)
   * @param streamId Stream ID to cancel
   * @returns Promise that resolves when stream is cancelled
   */
  async cancelStream(streamId: string): Promise<void> {
    try {
      logger.info('Cancelling scheduled stream:', streamId);
      
      // Use end stream endpoint to cancel/end the stream
      await this.endStream(streamId);
      
      logger.info('Stream cancelled successfully:', streamId);
    } catch (error: any) {
      logger.error('Failed to cancel stream:', error);
      
      if (error instanceof LiveStreamError) {
        throw error;
      }
      
      throw new LiveStreamError(
        LiveStreamErrorCode.IVS_SERVICE_ERROR,
        error.response?.data?.message || 'Failed to cancel stream. Please try again.',
        error.response?.data
      );
    }
  }

  /**
   * Check if user can send emoji (rate limiting)
   * @returns True if user can send emoji
   */
  canSendEmoji(): boolean {
    return this.emojiRateLimiter.canSendEmoji();
  }

  /**
   * Get time until next emoji can be sent
   * @returns Time in milliseconds
   */
  getTimeUntilNextEmoji(): number {
    return this.emojiRateLimiter.getTimeUntilNextEmoji();
  }

  /**
   * Get real-time stream metrics
   * @param streamId Stream ID
   * @returns Promise with stream metrics
   */
  async getStreamMetrics(streamId: string): Promise<{
    viewerCount: number;
    totalViewers: number;
    adRevenue: number;
    duration: number;
  }> {
    try {
      logger.debug('Getting stream metrics:', streamId);
      
      // In a real implementation, this would be a dedicated metrics endpoint
      // For now, we'll use the discover endpoint to get basic stream info
      const stream = await this.getStream(streamId);
      
      return {
        viewerCount: stream.maxViewers || 0,
        totalViewers: stream.totalViewers || 0,
        adRevenue: 0, // Would come from ad service
        duration: 0, // Would be calculated from startedAt
      };
    } catch (error: any) {
      logger.error('Failed to get stream metrics:', error);
      
      // Return default metrics on error to avoid breaking the UI
      return {
        viewerCount: 0,
        totalViewers: 0,
        adRevenue: 0,
        duration: 0,
      };
    }
  }

  /**
   * Get user-friendly error message for live stream errors
   * @param error Error object
   * @returns User-friendly error message
   */
  static getErrorMessage(error: any): string {
    if (error instanceof LiveStreamError) {
      return error.message;
    }
    
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    
    if (error.message) {
      return error.message;
    }
    
    return 'An unexpected error occurred. Please try again.';
  }
}

export default new LiveStreamService();